import { useCallback } from 'react';

import { chunk } from 'lodash-es';

import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import {
  httpGetGroupsBasic,
  httpGetRoleGroupsBasic,
  httpGetTeamsBasic,
  httpGetUsersBasic,
} from '@iconik-io/iconik-api/src/services/users';

import type { EndpointResponseNormalized } from '../../../redux/middlewares/http_normalizer/types';
import { useAppDispatch } from '../../../redux/redux_hooks';
import {
  type UseEntityOptions,
  type UseEntityResult,
  useEntity,
} from './use_entity';

const entityEndpointsMap = {
  users_basic: httpGetUsersBasic,
  groups_basic: httpGetGroupsBasic,
  teams_basic: httpGetTeamsBasic,
  role_groups_basic: httpGetRoleGroupsBasic,
};

type BatchEntityType = keyof typeof entityEndpointsMap;

type UseEntityBatchOptions<
  EntityType extends BatchEntityType,
  Result extends EndpointResponseNormalized,
> = Omit<UseEntityOptions<EntityType, Result>, 'getItem'>;

const entityIdsMap: Record<BatchEntityType, Set<string>> = {
  users_basic: new Set(),
  groups_basic: new Set(),
  teams_basic: new Set(),
  role_groups_basic: new Set(),
};

const entityQueuedIdsMap: Record<BatchEntityType, Set<string>> = {
  users_basic: new Set(),
  groups_basic: new Set(),
  teams_basic: new Set(),
  role_groups_basic: new Set(),
};

const entityInFlightMap: Record<BatchEntityType, boolean> = {
  users_basic: false,
  groups_basic: false,
  teams_basic: false,
  role_groups_basic: false,
};

const entityTimeoutMap = new Map<BatchEntityType, number>();

export function useEntityBatch<
  EntityType extends BatchEntityType,
  Result extends EndpointResponseNormalized,
>(
  options: UseEntityBatchOptions<EntityType, Result>,
): UseEntityResult<EntityType> {
  const dispatch = useAppDispatch();

  const getItem = useCallback(() => {
    const primaryId = options.primaryId;
    if (!primaryId) {
      return Promise.reject(new Error('Primary ID is not specified'));
    }
    clearTimeout(entityTimeoutMap.get(options.entityType));

    if (entityInFlightMap[options.entityType]) {
      entityQueuedIdsMap[options.entityType].add(primaryId);
    } else {
      entityIdsMap[options.entityType].add(primaryId);
    }

    const endpoint = entityEndpointsMap[options.entityType];

    return new Promise<EndpointResponse<ReturnType<typeof endpoint>>>(
      (resolve, reject) => {
        entityTimeoutMap.set(
          options.entityType,
          window.setTimeout(() => {
            const idChunkIndex: Record<string, number> = {};
            entityTimeoutMap.delete(options.entityType);

            const idChunks = chunk([...entityIdsMap[options.entityType]], 50);
            const promises = [];
            for (let i = 0; i < idChunks.length; i++) {
              const idChunk = idChunks[i];
              idChunk.forEach((id) => {
                idChunkIndex[id] = i;
              });
              promises.push(
                dispatch(
                  endpoint({
                    ids: idChunk.join(','),
                    per_page: 50,
                  }),
                ),
              );
            }

            entityInFlightMap[options.entityType] = true;

            Promise.all(promises)
              .then((result) => {
                const resultElement = result[idChunkIndex[primaryId]];
                resolve(
                  // biome-ignore lint/plugin: the batched promises share one endpoint, but Promise.all widens the results to their union.
                  resultElement as EndpointResponse<
                    ReturnType<typeof endpoint>
                  >,
                );

                entityIdsMap[options.entityType] =
                  entityQueuedIdsMap[options.entityType];

                entityQueuedIdsMap[options.entityType] = new Set();
              })
              .catch(reject)
              .finally(() => {
                entityInFlightMap[options.entityType] = false;
              });
          }, 50),
        );
      },
    );
  }, [options.primaryId, options.entityType]);

  return useEntity({
    entityType: options.entityType,
    primaryId: options.primaryId,
    getItem,
  });
}
